import { useState } from "react";
import { Mail, Phone, MapPin, Instagram, Linkedin, ArrowUpRight, Loader2, CheckCircle2, AlertCircle } from "lucide-react";
import { Reveal } from "./Reveal";
import { PROJECT_TYPES, BUDGETS } from "../data/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EMPTY = { name: "", email: "", phone: "", company: "", project_type: PROJECT_TYPES[0], budget: BUDGETS[0], message: "" };

export const Contact = () => {
  const [form, setForm] = useState(EMPTY);
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState("idle"); // idle | sending | success | error

  const set = (k) => (e) => {
    setForm({ ...form, [k]: e.target.value });
    if (errors[k]) setErrors({ ...errors, [k]: null });
  };

  const validate = () => {
    const errs = {};
    if (!form.name.trim()) errs.name = "Please tell us your name";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) errs.email = "Please enter a valid email";
    if (!form.message.trim()) errs.message = "Tell us a little about your project";
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setStatus("sending");
    try {
      const res = await fetch(`${API}/enquiries`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      if (!res.ok) throw new Error("Request failed");
      setStatus("success");
      setForm(EMPTY);
    } catch {
      setStatus("error");
    }
  };

  const field = (key, label, type = "text", testid) => (
    <div className="field">
      <label htmlFor={key}>{label}</label>
      <input
        type={type}
        id={key}
        value={form[key]}
        onChange={set(key)}
        className={errors[key] ? "invalid" : ""}
        data-testid={testid}
      />
      {errors[key] && <span className="field-error" data-testid={`${testid}-error`}>{errors[key]}</span>}
    </div>
  );

  return (
    <section className="section section-dark" id="contact" data-testid="contact-section">
      <div className="wrap contact-grid">
        <Reveal className="contact-info">
          <span className="eyebrow"><span className="sq" /> Get In Touch</span>
          <h2 data-testid="contact-heading">Let's Build Something Remarkable.</h2>
          <p>Whether you're launching a new brand, scaling your business, or looking for a long-term creative
            partner — we'd love to hear from you.</p>

          <div className="contact-detail" data-testid="contact-email-detail">
            <div className="ico"><Mail size={18} /></div>
            <div>
              <span className="detail-label">Email</span>
              <a href="mailto:ask.kr.studio@gmail.com" data-testid="contact-email-link">ask.kr.studio@gmail.com</a>
            </div>
          </div>
          <div className="contact-detail" data-testid="contact-phone-detail">
            <div className="ico"><Phone size={18} /></div>
            <div>
              <span className="detail-label">Phone</span>
              <a href="tel:+917006676788" data-testid="contact-phone-link">+91 70066 76788</a>
            </div>
          </div>
          <div className="contact-detail" data-testid="contact-location-detail">
            <div className="ico"><MapPin size={18} /></div>
            <div>
              <span className="detail-label">Location</span>
              <p>Delhi NCR, India — serving clients across India and globally.</p>
            </div>
          </div>

          <div className="social-row">
            <a href="https://www.instagram.com/the.krstudio/" target="_blank" rel="noopener noreferrer" data-testid="social-instagram">
              <Instagram size={16} /> Instagram
            </a>
            <a href="https://www.linkedin.com/company/kr-studio-india/" target="_blank" rel="noopener noreferrer" data-testid="social-linkedin">
              <Linkedin size={16} /> LinkedIn
            </a>
          </div>
        </Reveal>

        <Reveal delay={120} className="form-card-wrap">
          <div className="form-card" data-testid="enquiry-form-card">
            {status === "success" ? (
              <div className="form-success" data-testid="form-success-message">
                <CheckCircle2 size={44} strokeWidth={1.8} />
                <h3>Enquiry sent.</h3>
                <p>Thanks for reaching out — your message has landed in our inbox. We usually respond
                  within 1–2 business days.</p>
                <button className="btn btn-outline-dark" onClick={() => setStatus("idle")} data-testid="form-send-another-btn">
                  Send another enquiry
                </button>
              </div>
            ) : (
              <form onSubmit={submit} noValidate data-testid="enquiry-form">
                <div className="form-row">
                  {field("name", "Full name", "text", "form-name-input")}
                  {field("email", "Email", "email", "form-email-input")}
                </div>
                <div className="form-row">
                  {field("phone", "Phone", "tel", "form-phone-input")}
                  {field("company", "Company", "text", "form-company-input")}
                </div>
                <div className="form-row">
                  <div className="field">
                    <label htmlFor="project_type">Project type</label>
                    <select id="project_type" value={form.project_type} onChange={set("project_type")} data-testid="form-project-type-select">
                      {PROJECT_TYPES.map((p) => <option key={p}>{p}</option>)}
                    </select>
                  </div>
                  <div className="field">
                    <label htmlFor="budget">Budget range</label>
                    <select id="budget" value={form.budget} onChange={set("budget")} data-testid="form-budget-select">
                      {BUDGETS.map((b) => <option key={b}>{b}</option>)}
                    </select>
                  </div>
                </div>
                <div className="field">
                  <label htmlFor="message">Tell us about your project</label>
                  <textarea
                    id="message"
                    rows={5}
                    value={form.message}
                    onChange={set("message")}
                    className={errors.message ? "invalid" : ""}
                    data-testid="form-message-textarea"
                  />
                  {errors.message && <span className="field-error" data-testid="form-message-textarea-error">{errors.message}</span>}
                </div>

                {status === "error" && (
                  <div className="form-error" data-testid="form-error-message">
                    <AlertCircle size={16} /> Something went wrong while sending. Please try again, or email us
                    directly at ask.kr.studio@gmail.com.
                  </div>
                )}

                <button type="submit" className="btn btn-primary btn-full" disabled={status === "sending"} data-testid="form-submit-btn">
                  {status === "sending" ? (
                    <>Sending <Loader2 size={16} className="spin" /></>
                  ) : (
                    <>Send enquiry <ArrowUpRight size={17} strokeWidth={2.5} /></>
                  )}
                </button>
                <p className="form-note" data-testid="form-helper-text">
                  Submissions are sent straight to ask.kr.studio@gmail.com. We usually respond within 1–2
                  business days.
                </p>
              </form>
            )}
          </div>
        </Reveal>
      </div>
    </section>
  );
};
